// 連線租期續租（plan/vms-system-v0.md §5.1）：媒體信令（signal.ts）與 VMS 事件串流（stream.ts）同一套 wire。
//
//   ← signin_ok {token_exp, renew_supported:true}                連線租期 = signin 那顆 access token 的 exp
//   ← lease_expiring {token_exp, expires_in_seconds}             到期前 60 秒（每個 exp 一次）
//   → renew {request_id, access_token}                           先 refresh，在同一條連線送新 token
//   ← response {request_id, command:"renew", status:"ok", payload:{token_exp}}
//     | error {request_id, code}                                 UNAUTHORIZED / FORBIDDEN / AUTHORIZATION_UNAVAILABLE / INVALID_MESSAGE / TOKEN_EXPIRED
//
// 沒續到的話，device-gateway `media.enforce_token_expiry` 開啟時到期以 1008 `token_expired` 關線（兩條連線同一個開關；close reason 不是錯誤碼）。

import { refresh, session } from './auth';
import { KinioError } from './errors';

/** 續租失敗時隔多久再試（租期還沒到就繼續試）。 */
export const LEASE_RENEW_RETRY_MS = 10_000;

/**
 * 續租要用的 access token：session 裡的 token 比租期（`tokenExp`，unix 秒）晚超過 `marginMs`（別處剛 refresh 過）
 * 就直接用，不再多打一次 identity；否則 refresh（單飛，跟 API 401 的 refresh 共用）。
 */
export async function leaseToken(tokenExp: number, marginMs = 60_000): Promise<string> {
  const cur = session.get();
  if (cur && tokenExp > 0 && cur.expires_at > tokenExp * 1000 + marginMs) return cur.access_token;
  return refresh();
}

/** 伺服端的 error 幀 → KinioError（409 + 錯誤碼，例如 AUTHORIZATION_UNAVAILABLE；碼一律 UPPER_SNAKE，SPEC #eng-10）。 */
export function leaseFrameError(f: { code?: unknown; message?: unknown }): KinioError {
  const code = (typeof f.code === 'string' && f.code) || 'SIGNAL_ERROR';
  return new KinioError(409, code, typeof f.message === 'string' ? f.message : code);
}

/**
 * 續租失敗之後要不要隔 LEASE_RENEW_RETRY_MS 再試：暫時性的（refresh 網路錯 / 5xx、AUTHORIZATION_UNAVAILABLE、逾時）
 * 而且租期還剩超過一個重試間隔。FORBIDDEN / UNAUTHORIZED / TOKEN_EXPIRED 不再試（到期由伺服端關線，自動重連重新 signin）。
 */
export function leaseRenewRetryable(e: unknown, tokenExp: number, now = Date.now()): boolean {
  const left = tokenExp > 0 ? tokenExp * 1000 - now : 0;
  const terminal = e instanceof KinioError && e.status > 0 && e.status < 500 && e.code !== 'AUTHORIZATION_UNAVAILABLE' && e.code !== 'COMMAND_TIMEOUT';
  return !terminal && left > LEASE_RENEW_RETRY_MS;
}
